import { useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SidebarProvider } from "./context/SidebarContext";
import { AuthProvider }    from "./context/AuthContext";
import { DaemonProvider }  from "./context/DaemonContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import ProtectedRoute      from "./components/ProtectedRoute";
import RequireModule       from "./components/RequireModule";
import { MODULES }         from "./features/auth/model/usePermissions";
import Layout       from "./components/Layout";
import Login        from "./features/auth/pages/LoginPage";
import Dashboard    from "./Pages/Dashboard";
import Connect      from "./Pages/Connect";
import Nodes        from "./Pages/Nodes";
import Users        from "./Pages/Users";
import Analytics    from "./Pages/Analytics";
import Settings     from "./Pages/Settings";
import AcceptInvite    from "./features/auth/pages/AcceptInvitePage";
import ForgotPassword  from "./features/auth/pages/ForgotPasswordPage";
import ResetPassword   from "./features/auth/pages/ResetPasswordPage";
import PlaceholderPage from "./Pages/PlaceholderPage";
import { Icons } from "./components/Icons";
import "./styles/globals.css";

// ── Inner app — rendered inside BrowserRouter so DaemonProvider can call
//    useNavigate if needed in the future, and AuthProvider can use hooks.
function InnerApp() {
  // dashboardRefreshRef lets DaemonProvider signal Dashboard to re-fetch
  // without creating a circular dependency. Dashboard registers its refresh
  // callback here; DaemonProvider calls it on vpn:backendDisconnected.
  const dashboardRefreshRef = useRef(null);

  // Stable reference — dashboardRefreshRef never changes, so deps are empty.
  // Without useCallback, every InnerApp re-render creates a new function
  // reference, which flows into DaemonContext.recheckDaemon's dep array,
  // tearing down and recreating the WebSocket on every render and dropping
  // vpn:statusChanged events in between.
  const handleBackendDisconnected = useCallback(() => {
    dashboardRefreshRef.current?.();
  }, []);

  return (
    <AuthProvider>
      <DaemonProvider onBackendDisconnected={handleBackendDisconnected}>
        <Routes>

          {/* ── Public routes ── */}
          {/* Enterprise access is invitation-based: login + invite-accept only, no self-service sign-up. */}
          <Route path="/login"              element={<Login />} />
          <Route path="/forgot"             element={<ForgotPassword />} />
          <Route path="/reset-password"     element={<ResetPassword />} />
          <Route path="/auth/accept-invite" element={<AcceptInvite />} />

          {/* ── Protected routes (need token) ── */}
          <Route path="/" element={
            <ProtectedRoute>
              <NotificationsProvider>
                <SidebarProvider>
                  <Layout />
                </SidebarProvider>
              </NotificationsProvider>
            </ProtectedRoute>
          }>
            {/* Dashboard, Connect and Compliance are not RBAC-gated modules. */}
            <Route index             element={<Dashboard refreshRef={dashboardRefreshRef} />} />
            <Route path="connect"    element={<Connect />} />
            <Route path="compliance" element={<PlaceholderPage title="Compliance" icon={Icons.compliance} />} />

            {/* The 5 RBAC modules — blocked (redirect home) when the role has None access. */}
            <Route path="nodes"      element={<RequireModule module={MODULES.NODE_MONITORING}><Nodes /></RequireModule>} />
            <Route path="users"      element={<RequireModule module={MODULES.USER_MANAGEMENT}><Users /></RequireModule>} />
            <Route path="analytics"  element={<RequireModule module={MODULES.ANALYTICS}><Analytics /></RequireModule>} />
            <Route path="settings"   element={<RequireModule module={MODULES.SETTINGS}><Settings /></RequireModule>} />
            <Route path="policies"   element={<RequireModule module={MODULES.POLICY_MANAGEMENT}><PlaceholderPage title="Policies" icon={Icons.policies} /></RequireModule>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />

        </Routes>
      </DaemonProvider>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <InnerApp />
    </BrowserRouter>
  );
}
